import React from "react";
import {
  FaGithub,
  FaLinkedin,
  FaSpotify,
  FaSoundcloud,
  FaMusic,
  FaYoutube,
  FaGoogleScholar,
  FaBluesky,
} from "react-icons/fa6";
import {
  Text
} from "@chakra-ui/react";

import NYUImg from "assets/images/experience/nyu.jpg";
import StanfordImg from "assets/images/experience/stanford.png";
import TwImg from "assets/images/experience/twitter.png";
import NKIImg from "assets/images/experience/nki.png";
import BraidImg from "assets/images/experience/braid.png";

const siteConfig = {
  author: {
    name: "Daniel Haşegan",
    tagline: "computational / cognitive neuroscience graduate student at NYU",
    accounts: [
      {
        url: "https://scholar.google.com/citations?hl=en&user=wJd-E18AAAAJ",
        label: "Google Scholar",
        icon: <FaGoogleScholar />
      },
      {
        url: "https://github.com/dhasegan",
        label: "GitHub",
        icon: <FaGithub />
      },
      {
        url: "https://bsky.app/profile/dhasemusic.bsky.social",
        label: "Bluesky",
        icon: <FaBluesky />
      },
      {
        url: "https://linkedin.com/in/dhasegan",
        label: "LinkedIn",
        icon: <FaLinkedin />
      }
    ]
  },
  pages: [
    { name: "Details", path: "/details", hint: "bio & research" },
    { name: "Publications", path: "/publications", hint: "papers & posters" },
    { name: "Experience", path: "/experience", hint: "research & industry" },
    { name: "Music", path: "/music", hint: "bands & solo work" },
    { name: "CV", path: "/cv.pdf", hint: "pdf", external: true },
  ],
  publications: {
    'preprints': [
    ],
    'pubs': [
      {
        'year': 2024,
        'text': (<><Text as='b'>Haşegan, D.</Text>, Geniesse, C., Chowdhury, S. and Saggar, M., 2023.
                Deconstructing the Mapper algorithm to extract richer topological and temporal features
                from functional neuroimaging data. <Text as='i'>Network Neuroscience</Text>, pp.1-60.
                doi: <a href='https://doi.org/10.1162/netn_a_00403'>https://doi.org/10.1162/netn_a_00403</a></>)
      },
      {
        'year': 2023,
        'text': (<>Auernhammer, J., Bruno, J., Booras, A., McIntyre, C., <Text as='b'>Hasegan, D.</Text>,
                and Saggar, M., 2023. NeuroDesign: Greater than the Sum of Its Parts.
                In Design Thinking Research: Innovation–Insight–Then and Now (pp. 197-211).
                Cham: Springer Nature Switzerland.
                doi: <a href='https://doi.org/10.1007/978-3-031-36103-6_10'>https://doi.org/10.1007/978-3-031-36103-6_10</a></>)
      },
      {
        'year': 2022,
        'text': (<><Text as='b'>Haşegan, D.</Text>, Deible, M., Earl, C., D'Onofrio, D., Hazan, H., Anwar, H. and
                Neymotin, S.A., 2022. Training spiking neuronal networks to perform motor control using
                reinforcement and evolutionary learning. Frontiers in computational neuroscience, 16, p.1017284. 
                doi: <a href='https://doi.org/10.3389/fncom.2022.1017284'>https://doi.org/10.3389/fncom.2022.1017284</a></>)
      },
      {
        'year': 2022,
        'text': (<>Anwar, H., Caby, S., Dura-Bernal, S., D'Onofrio, D., <Text as='b'>Hasegan, D.</Text>, Deible, M., Grunblatt, S., Chadderdon, G.L., Kerr, C.C., Lakatos, P. and Lytton, W.W., 2022.
                Training a spiking neuronal network model of visual-motor cortex to play a virtual racket-ball game using reinforcement learning. <Text as='i'>PloS one</Text>, 17(5), p.e0265808.
                doi: <a href='https://doi.org/10.1371/journal.pone.0265808'>https://doi.org/10.1371/journal.pone.0265808</a></>)
      }
    ],
    'posters': [
      {
        'year': 2025,
        'text': (<><Text as='b'>Haşegan, D.</Text>, Hardstone R., He B.J. Traveling wave dynamics reflect perceptual events during bistable visual perception. <Text as='i'>Nanosymposium presentation at Society for Neuroscience conference (SfN) </Text> </>)
      },
      {
        'year': 2024,
        'text': (<><Text as='b'>Haşegan, D.</Text>, Hardstone R., He B.J. Traveling Waves of activity during bistable perception. <Text as='i'>NYU Neuroscience Graduate Program</Text></>)
      },
      {
        'year': 2022,
        'text': (<><Text as='b'>Haşegan, D.</Text>, Geniesse, C., Chowdhury S., Saggar M. Deconstructing Mapper to reveal the brain’s dynamic organization. <Text as='i'>Computational Psychiatry Course (CPC++)</Text></>)
      },
      {
        'year': 2021,
        'text': (<><Text as='b'>Haşegan, D.</Text>, D’Onofrio, D., Grunblatt, S., Deible, M., Caby, S., Hazan, H., Anwar, H., S.A. Neymotin.
                Using Spiking Neuronal Networks to solve Reinforcement Learning control problems. <Text as='i'>Society for Neuroscience conference (SfN)</Text></>)
      }
    ]
  },
  experience: [
    {
      img: NYUImg,
      role: "Graduate Student",
      org: "NYU Neuroscience program",
      url: "https://neuroscience.nyu.edu/program.html",
      sections: [
        "Pursuing specialization in the Systems, Cognitive, and Computational (SCC) track to delve into the multifaceted dimensions of neuroscience.",
        "Engaged in rigorous coursework and research to build a solid foundation in understanding brain dynamics and cognitive processes.",
        "Joined Dr. Biyu He's Lab as a Graduate Student"
      ],
      years: "2022 – present"
    },
    {
      img: StanfordImg,
      role: "Research Data Analyst II",
      org: "Brain Dynamics Lab, Stanford",
      url: "https://braindynamicslab.github.io/",
      sections: [
        "Collaborated within Dr. Manish Saggar’s Lab to process and analyze extensive fMRI datasets, each comprising over 1000 subjects.",
        "Employed cutting-edge tools including Mapper from Topological Data Analysis (TDA) to gain insights into the intricate topology of brain patterns.",
        "Contributed as a Level 2 operator, performing MRI scans for research purposes.",
      ],
      years: "2021 – 2022"
    },
    {
      img: NKIImg,
      role: "Collaborator",
      org: "Nathan S. Kline Institute for Psychiatric Research, NY",
      sections: [
        "Investigated Spiking Neuronal Networks, utilizing biologically-plausible learning rules such as Spike-Timing Dependent Plasticity, Reinforcement Learning, and Evolutionary Strategies.",
        "Focused on solving control problems like CartPole from OpenAI Gym, collaborating closely with Dr. Samuel Neymotin and Dr. Haroon Anwar."
      ],
      years: "2021 – 2022"
    },
    {
      img: BraidImg,
      role: "Lead Data Software Engineer",
      org: "Braid Health, San Francisco",
      url: "https://braid.health",
      sections: [
        "Played a foundational role as the first employee after the company's inception, establishing critical infrastructure and steering Data and Machine Learning initiatives.",
        "Designed and operationalized advanced X-Ray disease detection models, enhancing diagnostic accuracy and efficiency for clinics and radiology departments.",
        "Spearheaded the creation of an automated data pipeline, unifying diverse data sources, facilitating model training, deployment, and testing. This pivotal pipeline accelerated exploration of model architecture and hyperparameters.",
        "Innovated with novel models for musculoskeletal radiographs across multiple view positions.",
        "To improve application and data integrity, I developed image preprocessing algorithms, migrated front-end models(WebDNN to TensorflowJS), optimized data structures, and increased the interpretability of Machine Learning results.",
        "I maintained Site Reliability by supporting customers, evaluating failure modes, and planning team objectives.",
      ],
      years: "2018 – 2021"
    },
    {
      img: TwImg,
      role: "Staff ML Software Engineer",
      org: "Twitter, San Francisco",
      sections: [
        "Contributed to the Content Understanding and Applied Deep Learning team, pioneering advancements in Named Entity Recognition (NER) models and their applications in Twitter Trends and Email Recommendations",
        "I championed the utilization of TensorFlow as a premier Machine Learning Framework, subsequently becoming the standard framework for ML at Twitter. After that project, I was actively involved in ML platform tooling.",
        "As part of the Twitter Cortex team, I developed short clip generation from Periscope video streams, branded as Periscope Replay Highlights. I also worked on servicing and training pipelines for multiple Machine Learning models for abusive content, image and video classifcation, and Not Safe For Work (and further Not Safe For Ads) tweets and users.",
        "I created a human-in-the-loop pipeline to evaluate the efficiency of ML models on a weekly basis. I supervised an intern in building an Active Learning pipeline that uses the weekly labeled results to update the trained model.",
        "In collaboration with the Periscope Team and AR/VR Director, I built a plugin in Unity that injects live feedback (comments and hearts) into a Virtual Reality environment for an immersive VR streaming experience.",
      ],
      years: "2014 – 2018"
    }
  ],
  music: {
    intremor: {
      links: [
        {
          url: "https://open.spotify.com/artist/16sBzFXMytMPMcFLEGnQpA?si=I20eOV85Tp2RInsYHeOzEA",
          label: "Spotify",
          icon: <FaSpotify />
        },
        {
          url: "https://www.youtube.com/channel/UCSryEdo1pzVVgBmwEpZBHNA",
          label: "YouTube",
          icon: <FaYoutube />
        },
        {
          url: "https://music.apple.com/us/album/intentiontremor-ep/1481518298?at=1001lbRT&ct=333102",
          label: "Apple Music",
          icon: <FaMusic />
        },
        {
          url: "https://soundcloud.com/intentiontremor",
          label: "SoundCloud",
          icon: <FaSoundcloud />
        },
      ]
    }
  }
};

export default siteConfig;
